"use client";

import { useRouterState } from "@tanstack/react-router";

import {
  FeedFilledIcon,
  FeedIcon,
  HomeFilledIcon,
  HomeIcon,
  LoginIcon,
  PaidFilledIcon,
  PaidIcon,
} from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { LoginLink } from "@/components/login-link";
import { shouldShowCommunityNavigation } from "@/components/should-show-community-navigation";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";

export interface BottomNavLabels {
  home: string;
  offers: string;
  community: string;
  signIn: string;
}

interface BottomNavItem {
  activeIcon: AppIcon;
  icon: AppIcon;
  label: string;
  href: string;
}

interface BottomNavClientProps {
  labels: BottomNavLabels;
}

export function BottomNavClient({ labels }: BottomNavClientProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: session } = useSession();
  const showCommunityNavigation = shouldShowCommunityNavigation(session?.user);

  const items: BottomNavItem[] = [
    { activeIcon: HomeFilledIcon, icon: HomeIcon, label: labels.home, href: "/" },
    { activeIcon: PaidFilledIcon, icon: PaidIcon, label: labels.offers, href: "/offers" },
  ];
  if (showCommunityNavigation) {
    items.push({
      activeIcon: FeedFilledIcon,
      icon: FeedIcon,
      label: labels.community,
      href: "/community",
    });
  }

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] xl:hidden">
      <div className="mx-auto flex max-w-lg items-center justify-around">
        {items.map(({ activeIcon, icon, label, href }) => {
          const isActive = href === "/" ? pathname === "/" : pathname?.startsWith(href);
          const Icon = isActive ? activeIcon : icon;
          return (
            <Link
              key={href}
              to={href}
              className={cn(
                "flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors",
                isActive ? "text-primary" : "text-muted-foreground",
              )}
            >
              <Icon className="text-[22px]" />
              {label}
            </Link>
          );
        })}
        {!session && (
          <LoginLink
            className={cn(
              "flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors",
              pathname?.startsWith("/login") ? "text-primary" : "text-muted-foreground",
            )}
          >
            <LoginIcon style={{ fontSize: 22 }} />
            {labels.signIn}
          </LoginLink>
        )}
      </div>
    </nav>
  );
}
